import React, { useEffect } from "react"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import { footerUrl, headerUrl } from "@/constants"
import {
  fetchPageModel,
  fetchPageShell,
} from "@/services/server/aemPageData.server"

import {
  removeAndCleanLink,
  filterBackEndJson,
  getDataLayerObj,
} from "@/utils/helper"
import { handleServerSideRouteError } from "@/utils/serverSideError"

import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import ShopTheRoomDetails from "@/components/ShopTheRoomDetails/ShopTheRoomDetails"
import getSelectedRoomAndSKUDetails from "@/services/lw.service"
import { normalizeRoomAndSkuDetailsResponse } from "@/responseProcessors/shopTheRoomResponseProcessor"
import usePageAuth from "@/hooks/usePageAuth"

/**
 * Shop The Room Details Page
 * Renders the Shop The Room details with SSR and client-side hydration for inventory/price.
 * @param {Object} props - Page props
 * @return {JSX.Element}
 */
function Page({
  tmpData: data,
  headertmp: header,
  footertmp: footer,
  globalScript,
  footerScript,
  roomData = {},
  analyticsData = {},
}) {
  usePageAuth()
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  return (
    <>
      {header ? (
        <GlobalPageHeader
          header={header}
          title={data.title}
          globalScripting={globalScript}
        />
      ) : null}
      <ShopTheRoomDetails roomData={roomData} />
      {footer ? (
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      ) : null}
    </>
  )
}

/**
 * Fetches all required data for SSR, normalizes API responses, and passes to the page as props.
 * @param {Object} context - Next.js SSR context
 * @return {Promise<{props: Object}>}
 */
export async function getServerSideProps({ req, locale }) {
  await i18n?.reloadResources()

  const requestUrl = req.url
  const pathWithoutQuery = requestUrl.split("?")[0]
  const slugValue = pathWithoutQuery.split("/").pop() ?? ""
  const reqUrl = pathWithoutQuery.split(`/${slugValue}`)[0]

  let data
  try {
    data = await fetchPageModel(`/${locale}${reqUrl}.model.json`)
  } catch (err) {
    return handleServerSideRouteError(
      "Shop the room detail page model fetch failed",
      err,
      {
        url: `/${locale}${reqUrl}.model.json`,
        requestUrl,
      }
    )
  }

  const { redirectUrl: redirectURL = "", redirectType: redirectStatus = "" } =
    data
  if (redirectURL) {
    return {
      redirect: {
        destination: removeAndCleanLink(redirectURL),
        permanent: redirectStatus === "301",
      },
    }
  }

  const { header, footer } = await fetchPageShell({
    headerModelUrl: `${headerUrl}.model.json`,
    footerModelUrl: `${footerUrl}.model.json`,
    pageName: "Shop the room detail",
    context: { locale, requestUrl },
  })
  /**
   * Calling Room API and fetching Room details and its products/skus details.
   *
   * @params {string} RoomID_s or slug_s - need to pass RoomID_s or slug_s argument
   * @returns {object}
   */
  const roomAndSkusData = await getSelectedRoomAndSKUDetails(slugValue) // fresh-pop, layered-warmth, soothing-retreat
  const roomAndSkusDetails = normalizeRoomAndSkuDetailsResponse(roomAndSkusData)

  const globalScript = data?.globalScript ? data?.globalScript : null
  const footerScript = data?.footerScript ? data?.footerScript : null

  let tmpData = {}
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }

  tmpData = filterBackEndJson(tmpData)
  if (data?.pageUrl) {
    tmpData["pageUrl"] = data?.pageUrl
  }

  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const { data: analyticsData = {} } = data
  // Pass data to the page via props
  return {
    props: {
      tmpData,
      headertmp,
      footertmp,
      globalScript,
      footerScript,
      roomData: roomAndSkusDetails,
      analyticsData,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}

export default Page
